<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import BridgeToolsAPI from 'dashboard/api/bridgeTools';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
  contact: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();

// --- Subscription lookup ---
const email = ref(props.contact?.email || '');
const subscription = ref(null);
const subLoading = ref(false);
const subError = ref('');

const lookupSubscription = async () => {
  const target = email.value.trim();
  if (!target) {
    subError.value = 'CONVERSATION_SIDEBAR.BRIDGE_TOOLS.NO_EMAIL';
    subscription.value = null;
    return;
  }
  try {
    subLoading.value = true;
    subError.value = '';
    const response = await BridgeToolsAPI.subscriptionLookup(target);
    if (email.value.trim() !== target) return; // conversation switched mid-flight
    subscription.value = response.data;
  } catch (e) {
    if (email.value.trim() !== target) return;
    if (e.response?.status === 404) {
      // No subscription record => treat the contact as a Free-tier user.
      subscription.value = { plan_name: null, subscription_status: false };
    } else {
      subscription.value = null;
      subError.value = 'CONVERSATION_SIDEBAR.BRIDGE_TOOLS.SUBSCRIPTION_ERROR';
    }
  } finally {
    subLoading.value = false;
  }
};

// Auto-fetch when the contact's email becomes available / changes.
watch(
  () => props.contact?.email,
  newEmail => {
    if (newEmail && newEmail !== email.value) {
      email.value = newEmail;
    }
    if (newEmail) {
      lookupSubscription();
    }
  },
  { immediate: true }
);

// --- Share link ---
const linkLoading = ref(false);
const linkError = ref('');

// Switching conversations must not carry over state from the previous one —
// the email watcher above never fires when the new contact has no email.
watch(
  () => props.conversationId,
  () => {
    linkError.value = '';
    const contactEmail = props.contact?.email || '';
    if (email.value !== contactEmail) {
      email.value = contactEmail;
      subscription.value = null;
      subError.value = '';
    }
  }
);

// The link is deterministic per conversation, so there is nothing worth
// keeping on screen: fetch it and put it straight on the clipboard. No
// persistent link state also means nothing stale to show after switching
// conversations.
const fetchAndCopyShareLink = async () => {
  try {
    linkLoading.value = true;
    linkError.value = '';
    const response = await BridgeToolsAPI.shareLink(props.conversationId);
    await copyTextToClipboard(response.data.link);
    useAlert(t('CONVERSATION_SIDEBAR.BRIDGE_TOOLS.COPIED'));
  } catch (e) {
    linkError.value = 'CONVERSATION_SIDEBAR.BRIDGE_TOOLS.SHARE_LINK_ERROR';
  } finally {
    linkLoading.value = false;
  }
};

const subscriptionActive = computed(
  () => subscription.value?.subscription_status === true
);
</script>

<template>
  <div class="flex flex-col gap-4 px-4 py-2 text-n-slate-12">
    <!-- Subscription lookup -->
    <div class="flex flex-col gap-2">
      <div class="flex gap-2">
        <input
          v-model="email"
          type="email"
          :placeholder="
            $t('CONVERSATION_SIDEBAR.BRIDGE_TOOLS.EMAIL_PLACEHOLDER')
          "
          class="reset-base flex-1 px-2 py-1 text-sm rounded-md border border-n-weak bg-n-alpha-black2 text-n-slate-12"
          @keyup.enter="lookupSubscription"
        />
        <button
          class="px-3 py-1 text-sm font-medium rounded-md border border-transparent bg-n-brand text-white disabled:opacity-50"
          :disabled="subLoading"
          @click="lookupSubscription"
        >
          {{ $t('CONVERSATION_SIDEBAR.BRIDGE_TOOLS.LOOKUP') }}
        </button>
      </div>

      <div v-if="subLoading" class="flex justify-center items-center p-2">
        <Spinner size="24" class="text-n-brand" />
      </div>
      <div v-else-if="subError" class="text-sm text-n-ruby-12">
        {{ $t(subError) }}
      </div>
      <div v-else-if="subscription" class="flex flex-col gap-1 text-sm">
        <div class="flex justify-between">
          <span class="text-n-slate-11">
            {{ $t('CONVERSATION_SIDEBAR.BRIDGE_TOOLS.PLAN') }}
          </span>
          <span class="font-medium">{{
            subscription.plan_name ||
            $t('CONVERSATION_SIDEBAR.BRIDGE_TOOLS.FREE')
          }}</span>
        </div>
        <div class="flex justify-between">
          <span class="text-n-slate-11">
            {{ $t('CONVERSATION_SIDEBAR.BRIDGE_TOOLS.STATUS') }}
          </span>
          <span
            class="font-medium"
            :class="subscriptionActive ? 'text-n-teal-11' : 'text-n-ruby-11'"
          >
            {{
              subscriptionActive
                ? $t('CONVERSATION_SIDEBAR.BRIDGE_TOOLS.ACTIVE')
                : $t('CONVERSATION_SIDEBAR.BRIDGE_TOOLS.INACTIVE')
            }}
          </span>
        </div>
        <div
          v-if="subscription.plan_next_due_date"
          class="flex justify-between"
        >
          <span class="text-n-slate-11">
            {{ $t('CONVERSATION_SIDEBAR.BRIDGE_TOOLS.NEXT_DUE') }}
          </span>
          <span class="font-medium">{{ subscription.plan_next_due_date }}</span>
        </div>
      </div>
    </div>

    <!-- Share link -->
    <div class="flex flex-col gap-2 pt-3 border-t border-n-weak">
      <button
        class="flex gap-2 justify-center items-center px-3 py-1.5 text-sm font-medium rounded-md bg-n-alpha-2 text-n-slate-12 disabled:opacity-50"
        :disabled="linkLoading"
        @click="fetchAndCopyShareLink"
      >
        <Spinner v-if="linkLoading" size="14" />
        {{ $t('CONVERSATION_SIDEBAR.BRIDGE_TOOLS.GENERATE_SHARE_LINK') }}
      </button>
      <div v-if="linkError" class="text-sm text-n-ruby-12">
        {{ $t(linkError) }}
      </div>
    </div>
  </div>
</template>
